import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Interceptor que agrega el header Accept-Language a todas las solicitudes.
 *
 * Obtiene el idioma actual de la sesión o configuración del usuario
 * y lo propaga en el header Accept-Language para que el backend
 * retorne respuestas en el idioma correcto.
 *
 * Valores soportados: es, en, pt, fr, de, etc. (ISO 639-1)
 */
@Injectable()
export class LanguageInterceptor implements HttpInterceptor {
  constructor() {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Obtener el idioma actual: localStorage, sessionStorage o detectar del navegador
    const language = this.getCurrentLanguage();
    const isHomeIaRequest =
      (environment.homeIaApiUrl && request.url.includes(environment.homeIaApiUrl)) ||
      request.url.includes('/api/home-ia');

    const safeLanguage = isHomeIaRequest ? this.normalizeHomeIaLanguage(language) : language;

    // Adjuntar el header Accept-Language
    const modifiedRequest = request.clone({
      setHeaders: {
        'Accept-Language': safeLanguage,
      },
    });

    return next.handle(modifiedRequest);
  }

  /**
   * Obtiene el idioma actual del usuario.
   *
   * Prioridad:
   * 1. localStorage['language'] - Preferencia guardada del usuario
   * 2. Idioma del navegador (navigator.language)
   * 3. Fallback: 'es' (español)
   *
   * @returns Código ISO 639-1 de idioma (ej: 'es', 'en', 'pt')
   */
  getCurrentLanguage(): string {
    // Intenta obtener del localStorage (preferencia guardada del usuario)
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      return this.normalizeLanguageCode(savedLanguage);
    }

    // Intenta obtener del idioma del navegador
    const browserLanguage = navigator.language || (navigator as any).userLanguage;
    if (browserLanguage) {
      return this.normalizeLanguageCode(browserLanguage);
    }

    // Fallback
    return 'es';
  }

  /**
   * Normaliza código de idioma a formato ISO 639-1.
   *
   * Ejemplos:
   * - 'es-ES' → 'es'
   * - 'en-US' → 'en'
   * - 'es' → 'es'
   *
   * @param languageCode Código de idioma potencialmente completo
   * @returns Código ISO 639-1 de dos letras
   */
  normalizeLanguageCode(languageCode: string): string {
    return languageCode.split(/[-_]/)[0].toLowerCase();
  }

  private normalizeHomeIaLanguage(languageCode: string): string {
    const normalized = this.normalizeLanguageCode(languageCode);
    if (normalized === 'en') {
      return 'en';
    }
    return 'es';
  }
}
